/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {Fragment} from 'react';
import {defineMessage, FormattedMessage, useIntl} from 'react-intl';
import {Link as RouteLink} from 'react-router-dom';
import PropTypes from 'prop-types';

import {SearchIcon} from '@salesforce/retail-react-app/app/components/icons';
import RecommendedProducts from '@salesforce/retail-react-app/app/components/recommended-products';
import {Button, Flex, Link, Stack, Text} from '@salesforce/retail-react-app/app/components/shared/ui';
import {EINSTEIN_RECOMMENDERS} from '@salesforce/retail-react-app/app/constants';

const contactUsMessage = defineMessage({
  id: 'empty_search_results.link.contact_us',
  defaultMessage: 'Contact Us',
});

const EmptySearchResults = ({searchQuery, category}) => {
  const intl = useIntl();

  return (
    <Flex
      alignItems="center"
      data-testid="sf-product-empty-list-page"
      direction="column"
      paddingBottom={28}
      paddingTop={28}
      textAlign="center"
    >
      <SearchIcon boxSize={[6, 12, 12, 12]} marginBottom={5} />
      {!searchQuery ? (
        <Fragment>
          {' '}
          <Text fontSize={['l', 'l', 'xl', '2xl']} fontWeight="700" marginBottom={2}>
            {intl.formatMessage(
              {
                id: 'empty_search_results.info.cant_find_anything_for_category',
                defaultMessage:
                  'We couldn’t find anything for {category}. Try searching for a product or {link}.',
              },
              {
                category: category?.name,
                link: (
                  <Link as={RouteLink} to="/">
                    {intl.formatMessage(contactUsMessage)}
                  </Link>
                ),
              }
            )}
          </Text>{' '}
        </Fragment>
      ) : (
        <Fragment>
          <Text fontSize={['lg', 'lg', 'xl', '3xl']} fontWeight="700" marginBottom={2}>
            {intl.formatMessage(
              {
                id: 'empty_search_results.info.cant_find_anything_for_query',
                defaultMessage: 'We couldn’t find anything for "{searchQuery}".',
              },
              {
                searchQuery: searchQuery,
              }
            )}
          </Text>
          <Text fontSize={['md', 'md', 'md', 'md']} fontWeight="400">
            {intl.formatMessage(
              {
                id: 'empty_search_results.info.double_check_spelling',
                defaultMessage:
                  'Double-check your spelling and try again or {link}.',
              },
              {
                link: (
                  <Button to="/" variant="link">
                    {intl.formatMessage(contactUsMessage)}
                  </Button>
                ),
              }
            )}
          </Text>
          <Stack marginTop={32} spacing={16}>
            <RecommendedProducts
              mx={{base: -4, md: -8, lg: 0}}
              recommender={EINSTEIN_RECOMMENDERS.EMPTY_SEARCH_RESULTS_TOP_SELLERS}
              title={
                <FormattedMessage
                  defaultMessage="Top Sellers"
                  id="empty_search_results.recommended_products.title.top_sellers"
                />
              }
            />

            <RecommendedProducts
              mx={{base: -4, md: -8, lg: 0}}
              recommender={EINSTEIN_RECOMMENDERS.EMPTY_SEARCH_RESULTS_MOST_VIEWED}
              title={
                <FormattedMessage
                  defaultMessage="Most Viewed"
                  id="empty_search_results.recommended_products.title.most_viewed"
                />
              }
            />
          </Stack>
        </Fragment>
      )}
    </Flex>
  );
};

EmptySearchResults.propTypes = {
  searchQuery: PropTypes.string,
  category: PropTypes.object,
};

export default EmptySearchResults;
